import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import type {
  UpdateUserProfileRequest,
  User,
  UserId,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import { ReachGrid } from "../../components/ReachGrid/ReachGrid.js";
import type { ProfileSaveOutcome } from "../../components/UserProfileForm/UserProfileForm.js";
import { UserProfileForm } from "../../components/UserProfileForm/UserProfileForm.js";
import { useUser } from "../../Context/UserContext.js";

export const ProfilePage = () => {
  const { user, refetchUser } = useUser();

  const save = async (
    userId: UserId,
    request: UpdateUserProfileRequest,
  ): Promise<ProfileSaveOutcome> => {
    const response = await api.user.updateProfile(userId, request);

    // 4xx resolves rather than throwing on this axios instance, so a refusal
    // has to be read off the status or the form settles as though it saved.
    // Only the status is mapped here; a 5xx or a dropped connection rejects
    // instead of arriving as one.
    if (response.status >= 400) {
      return "failed";
    }

    // The name is on the app bar and in the trail's record of everything this
    // person does next, so the session's copy of it has to catch up rather than
    // waiting for the next page load.
    await refetchUser();
    return "saved";
  };

  return <ProfilePageContent user={user} onSave={save} />;
};

/**
 * The page's rendering, with the signed-in user and the save injected rather
 * than reached for through context and the API client — so a story can show any
 * state it has without standing up either.
 *
 * Takes `User` rather than `CurrentUser`: this page is about who someone is and
 * says nothing about what they may do, and the narrower type keeps it that way.
 */
/**
 * Every young person this account reaches.
 *
 * Its own component so the read is made only once there is an account to make
 * it for. Called from the page, the hook fires on the first render — before the
 * session resolves — and asks for `/api/users//reach`, which matches no route.
 */
const WhatThisAccountReaches = ({ userId }: { userId: UserId }) => {
  const [{ data: reach, error, response }] =
    apiHooks.involvement.forUser(userId);

  return (
    <ReachGrid
      reach={reach}
      failed={error !== null && response?.status !== 200}
    />
  );
};

export const ProfilePageContent = ({
  user,
  onSave,
}: {
  user: User | undefined;
  /**
   * Takes the account's id as well as the values, so the id comes from the
   * account on screen rather than from a closure that has to allow for there
   * being no account yet.
   */
  onSave: (
    userId: UserId,
    request: UpdateUserProfileRequest,
  ) => Promise<ProfileSaveOutcome>;
}) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={3} sx={{ maxWidth: 900 }}>
      <Stack spacing={1}>
        <Typography variant="h4" component="h1">
          {t("profile.title")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("profile.description")}
        </Typography>
      </Stack>

      {/* The page can render for a moment before the session resolves. Saying
          so is the honest thing to show — a form seeded with an empty name
          would offer to overwrite the real one with it. */}
      {user === undefined ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : (
        <>
          <UserProfileForm
            user={user}
            onSave={(request) => onSave(user.id, request)}
          />
          <WhatThisAccountReaches userId={user.id} />
        </>
      )}
    </Stack>
  );
};
